import { type DeviceEntity } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { useInterval } from '@solinkcorp/solink-components';
import { getAccessibleAddress } from '@solinkcorp/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDeviceIsLocal } from '../contexts/DeviceContext';
import { getWidgetLatencyMs, safeValidateSdmEvent } from '../data-structures/sdmEventTypes';
import { type ServiceDisplayEventsController } from '../ServiceDisplayEventsController';
import { type SSEConnectionState, useServerSentEvents } from './useServerSentEvents';
import useServiceDisplayHistoricalEvents from './useServiceDisplayHistoricalEvents';

const ONE_HOUR_MS = 60 * 60 * 1000;

export default function useServiceDisplayEvents(
  device: DeviceEntity | undefined,
  eventsController: ServiceDisplayEventsController,
  /**
   * How far back (ms) widgets in the current layout may need data. The historical fetch
   * and the controller's retention window are sized to at least this much. Defaults to 1h.
   */
  lookbackMs: number = ONE_HOUR_MS,
): {
  isLoadingHistoricalEvents: boolean;
  sseStatus: SSEConnectionState['status'];
  lastEventReceivedAt: string | null;
  /**
   * Returns the mean SSE event latency (ms) accumulated since the previous call and resets the
   * window, so each heartbeat reports only the events received in its interval. Returns null when
   * no events arrived in the window.
   */
  consumeAvgEventLatencyMs: () => number | null;
} {
  const { accessToken, clientSettings } = useUserSession();
  const deviceId = device?.id;
  const isLocal = useDeviceIsLocal(deviceId);
  const accessibleAddress = device ? getAccessibleAddress(device, isLocal) : null;
  const initialTimeMs = useRef(Date.now()); // used to fetch historical data, used only once on mount.
  const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now()); // routinely set to trigger cleanup of historical data.
  const lastEventReceivedAt = useRef<string | null>(null);
  // Running accumulators for average SSE event latency over the life of this connection.
  const eventLatencySumMs = useRef(0);
  const eventLatencyCount = useRef(0);

  const effectiveLookbackMs = Math.max(ONE_HOUR_MS, lookbackMs);
  // maxOccupancyEnabled means the device can track concurrent occupancy; the controller then
  // should *not* assume FIFO order when clearing orphans on exit.
  const assumeFifoEventsOrder = !clientSettings?.maxOccupancyEnabled;

  // End timestamp for the historical fetch. Starts at mount and is bumped to `Date.now()`
  // whenever `effectiveLookbackMs` grows, so the refetch backfills any SSE-delivered events
  // that may have been pruned under the previous (narrower) windowRangeMs.
  // For example, if the SDM is is open with a widget covering a whole day and has received SSE events for 3h,
  // when a user shrinks the widgets to 1h, SSE events will be pruned.
  // if user then expands the widgets back to a full day, this prevents the historical fetch window
  // from ending to the open date 3h earlier and missing the t-3h -> t-1h events that have since been received but pruned
  const [historicalEndMs, setHistoricalEndMs] = useState(() => initialTimeMs.current);
  const prevEffectiveLookbackMs = useRef(effectiveLookbackMs);

  useEffect(() => {
    eventsController.setWindowRangeMs(effectiveLookbackMs);
    if (effectiveLookbackMs > prevEffectiveLookbackMs.current) {
      setHistoricalEndMs(Date.now());
    }
    prevEffectiveLookbackMs.current = effectiveLookbackMs;
  }, [eventsController, effectiveLookbackMs]);

  useEffect(() => {
    eventsController.setAssumeFifoEventsOrder(assumeFifoEventsOrder);
  }, [eventsController, assumeFifoEventsOrder]);

  const { data, isLoading: isLoadingHistoricalEvents } = useServiceDisplayHistoricalEvents(
    initialTimeMs.current - effectiveLookbackMs,
    historicalEndMs,
    accessibleAddress || '',
  );

  const [hasProcessedHistoricalData, setHasProcessedHistoricalData] = useState(false);
  // Tracks the exact `data` reference last fed into the controller. We compare by reference
  // so a wider re-fetch (e.g. when `effectiveLookbackMs` grows once dayParts load async) is
  // ingested exactly once even though `hasProcessedHistoricalData` is already true.
  // `processHistoricalEvents` dedupes by `evt_id`, so reprocessing a superset is safe.
  const lastProcessedDataRef = useRef<typeof data | null>(null);

  // Reset state when device changes to refetch historical data
  useEffect(() => {
    initialTimeMs.current = Date.now();
    setCurrentTimeMs(Date.now());
    setHistoricalEndMs(initialTimeMs.current);
    setHasProcessedHistoricalData(false);
    lastProcessedDataRef.current = null;
    lastEventReceivedAt.current = null;
    eventLatencySumMs.current = 0;
    eventLatencyCount.current = 0;
    eventsController.clearAllEvents();
  }, [deviceId, eventsController]);

  useEffect(() => {
    if (!data || lastProcessedDataRef.current === data) return;
    eventsController.processHistoricalEvents(data);
    lastProcessedDataRef.current = data;
    if (!hasProcessedHistoricalData) {
      setHasProcessedHistoricalData(true);
    }
  }, [data, hasProcessedHistoricalData, eventsController]);

  // periodically prune data for historical data freshness
  useInterval(
    () => eventsController.pruneMap(Date.now()),
    hasProcessedHistoricalData ? 6000 : undefined,
  );

  const onEvent = useCallback(
    (data: any) => {
      // Debug log the raw SSE event
      eventsController.debugLog('onEvent', 'Raw SSE event received', {
        rawData: data,
      });

      const parsed = JSON.parse(data);
      const result = eventsController.processEvent(parsed);

      const receivedAtMs = Date.now();
      lastEventReceivedAt.current = new Date(receivedAtMs).toISOString();

      // Accumulate widget latency only for events that newly updated widgets.
      // processEvent returns empty eventIds for SSE reconnect lookback duplicates (and
      // tagless events); counting those would inflate avgEventLatencyMs with large
      // receipt−event gaps. getWidgetLatencyMs returns null for batched line crossings,
      // which we also skip so they don't dilute the average.
      if (result.eventIds && result.eventIds.length > 0) {
        const validated = safeValidateSdmEvent(parsed);
        if (validated.success) {
          const latencyMs = getWidgetLatencyMs(validated.data, receivedAtMs);
          if (latencyMs !== null) {
            eventLatencySumMs.current += latencyMs;
            eventLatencyCount.current += 1;
          }
        }
      }
    },
    [eventsController],
  );

  const consumeAvgEventLatencyMs = useCallback((): number | null => {
    const count = eventLatencyCount.current;
    if (count === 0) return null;
    const avg = Math.round(eventLatencySumMs.current / count);
    eventLatencySumMs.current = 0;
    eventLatencyCount.current = 0;
    return avg;
  }, []);

  const onOpen = useCallback(() => {
    eventsController.debugLog('onEvent', 'Reconnecting SSE request');
  }, [eventsController]);

  const onError = useCallback(
    (e: Event) => {
      // On connection drop. Use this to refretch Server Sent Events.
      eventsController.debugLog('connection_error', 'SSE Connection dropped', { error: e });
      setCurrentTimeMs(Date.now());
    },
    [eventsController],
  );

  const { status: sseStatus } = useServerSentEvents({
    url: accessibleAddress
      ? `${accessibleAddress}/ai/modules/line_crossing/events?start=${currentTimeMs - 10 * 1000}`
      : '',
    accessToken,
    onOpen,
    onMessage: onEvent,
    onError,
  });

  return {
    isLoadingHistoricalEvents,
    sseStatus,
    lastEventReceivedAt: lastEventReceivedAt.current,
    consumeAvgEventLatencyMs,
  };
}
